import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './CPanel.css';

const HISTORY_WINDOWS = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' }
];

// Buckets a chronological list of checks down to a fixed number of bars for
// display, taking the max in each bucket - a brief spike inside a bucket
// should still show up as a spike, not get smoothed away by an average.
const BAR_COUNT = 60;
const bucketForChart = (checks) => {
  if (!checks.length) return [];
  const bucketSize = Math.max(1, Math.ceil(checks.length / BAR_COUNT));
  const buckets = [];
  for (let i = 0; i < checks.length; i += bucketSize) {
    const slice = checks.slice(i, i + bucketSize);
    const values = slice.map((c) => c.nproc_usage).filter((v) => v !== null && v !== undefined);
    buckets.push({
      value: values.length ? Math.max(...values) : null,
      at: slice[slice.length - 1].createdAt
    });
  }
  return buckets;
};

// Same cadence the server checks on, so a dashboard left open on a wall
// doesn't quietly go stale - matches Uptime.js.
const REFRESH_MS = 60000;

const METRIC_LABELS = {
  nproc: 'Processes',
  ep: 'Entry Procs',
  pmem: 'Memory',
  io: 'I/O',
  iops: 'IOPS',
  speed: 'CPU'
};
const METRIC_ORDER = ['nproc', 'ep', 'pmem', 'io', 'iops', 'speed'];

const formatWhen = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return date.toLocaleString();
};

// ok / warn / maxed drives both the row's badge and the process cell's
// color - 80% is a heads-up, 100%+ is the "stuck" state Slack alerts on.
const stateFor = (account) => {
  if (account.last_error) return 'error';
  if (!account.nproc_limit) return 'unknown';
  const ratio = account.nproc_usage / account.nproc_limit;
  if (ratio >= 1) return 'maxed';
  if (ratio >= 0.8) return 'warn';
  return 'ok';
};

const formatMetric = (metric) => {
  if (!metric) return '—';
  if (metric.limit !== null && metric.limit !== undefined) return `${metric.usage}/${metric.limit}`;
  if (metric.usage !== null && metric.usage !== undefined) return `${metric.usage}`;
  return '—';
};

function CPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState(1);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setData(await apiClient.get('/api/cpanel'));
      setError('');
    } catch (err) {
      console.error('Error loading cPanel accounts:', err);
      setError(err.message || 'Could not load cPanel accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const recheck = async (username) => {
    setChecking(username || 'all');
    try {
      await apiClient.post('/api/cpanel/check', username ? { username } : {});
      await load();
    } catch (err) {
      setError(err.message || 'Could not run that check.');
    } finally {
      setChecking(null);
    }
  };

  const loadHistory = useCallback(async (account, days) => {
    setDetailLoading(true);
    try {
      const history = await apiClient.get(`/api/cpanel/${encodeURIComponent(account.cpanel_username)}/history?days=${days}`);
      setDetail({ account, ...history });
    } catch (err) {
      setError(err.message || 'Could not load that history.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetail = (account) => {
    setHistoryDays(1);
    setDetail({ account, checks: [] });
    loadHistory(account, 1);
  };

  const changeHistoryWindow = (days) => {
    setHistoryDays(days);
    if (detail) loadHistory(detail.account, days);
  };

  const columns = [
    {
      key: 'cpanel_username',
      label: 'Account',
      sortable: true,
      render: (account) => {
        const state = stateFor(account);
        return (
          <div className="cpanel-account">
            <span className={`cpanel-dot cpanel-dot-${state}`} aria-hidden="true"></span>
            <div>
              <div className="cpanel-domain">{account.domain || account.cpanel_username}</div>
              <div className="cpanel-username">
                {account.cpanel_username}
                {account.suspended && <span className="cpanel-suspended-tag">suspended</span>}
              </div>
              {account.last_error && <div className="cpanel-reason">{account.last_error}</div>}
            </div>
          </div>
        );
      }
    },
    {
      key: 'state',
      label: 'Status',
      sortable: true,
      sortValue: (account) => stateFor(account),
      render: (account) => {
        const state = stateFor(account);
        return (
          <>
            <span className={`cpanel-badge cpanel-badge-${state}`}>{state}</span>
            {account.spike_alerted && <span className="cpanel-badge cpanel-badge-spike">⚡ spike</span>}
          </>
        );
      }
    },
    ...METRIC_ORDER.map((key) => ({
      key,
      label: METRIC_LABELS[key],
      sortable: key === 'nproc',
      sortValue: key === 'nproc' ? (account) => (account.nproc_limit ? account.nproc_usage / account.nproc_limit : -1) : undefined,
      render: (account) => {
        const metric = account.usage_snapshot?.[key];
        const text = formatMetric(metric);
        if (key !== 'nproc') return text;
        return <span className={`cpanel-nproc cpanel-nproc-${stateFor(account)}`}>{text}</span>;
      }
    })),
    {
      key: 'nproc_avg_24h',
      label: 'Avg (24h)',
      sortable: true,
      sortValue: (account) => (account.nproc_avg_24h === null || account.nproc_avg_24h === undefined ? -1 : account.nproc_avg_24h),
      render: (account) => (account.nproc_avg_24h === null || account.nproc_avg_24h === undefined ? '—' : account.nproc_avg_24h)
    },
    {
      key: 'last_checked_at',
      label: 'Checked',
      sortable: true,
      render: (account) => formatWhen(account.last_checked_at)
    },
    {
      key: 'actions',
      label: '',
      render: (account) => (
        <div className="cpanel-actions">
          <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openDetail(account); }}>
            History
          </Button>
          <Button
            onClick={(e) => { e.stopPropagation(); recheck(account.cpanel_username); }}
            disabled={checking === account.cpanel_username}
          >
            {checking === account.cpanel_username ? 'Checking...' : 'Re-check'}
          </Button>
        </div>
      )
    }
  ];

  const summary = data?.summary;
  const notConfigured = data && data.configured === false;

  return (
    <PageShell>
      <div className="cpanel-header">
        <div>
          <h1>cPanel</h1>
          <p className="cpanel-intro">
            Every cPanel account on the server is checked every 5 minutes for its CloudLinux resource
            usage, and every check is kept for 30 days so trends and averages are visible, not just
            the latest snapshot.
            {data && ` Slack is notified after ${data.alert_after_maxed} consecutive checks at the process (NPROC) limit, and again if usage spikes well above an account's own recent average.`}
          </p>
        </div>
        <Button onClick={() => recheck(null)} disabled={checking !== null || notConfigured}>
          {checking === 'all' ? 'Checking...' : 'Check all now'}
        </Button>
      </div>

      {notConfigured && (
        <div className="cpanel-error">
          WHM is not configured on this instance (WHM_HOST / WHM_USER / WHM_API_TOKEN) - this page has nothing to show until it is.
        </div>
      )}
      {error && <div className="cpanel-error">{error}</div>}

      {summary && (
        <div className="cpanel-summary">
          <div className="cpanel-card">
            <span className="cpanel-card-value">{summary.total}</span>
            <span className="cpanel-card-label">Accounts</span>
          </div>
          <div className={`cpanel-card ${summary.maxed > 0 ? 'cpanel-card-maxed' : ''}`}>
            <span className="cpanel-card-value">{summary.maxed}</span>
            <span className="cpanel-card-label">At process limit</span>
          </div>
          <div className={`cpanel-card ${summary.spiking > 0 ? 'cpanel-card-spiking' : ''}`}>
            <span className="cpanel-card-value">{summary.spiking}</span>
            <span className="cpanel-card-label">Spiking</span>
          </div>
          <div className="cpanel-card">
            <span className="cpanel-card-value">{summary.errored}</span>
            <span className="cpanel-card-label">Check errors</span>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading cPanel accounts...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={data?.accounts || []}
          rowKey="cpanel_username"
          emptyMessage={notConfigured ? '' : 'No accounts checked yet — click "Check all now" or wait for the next 5-minute pass.'}
        />
      )}

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? (detail.account.domain || detail.account.cpanel_username) : ''}
        size="large"
      >
        {detail && (
          <div className="cpanel-detail">
            <div className="cpanel-detail-windows">
              {HISTORY_WINDOWS.map((w) => (
                <button
                  key={w.days}
                  type="button"
                  className={`cpanel-window-btn ${historyDays === w.days ? 'is-active' : ''}`}
                  onClick={() => changeHistoryWindow(w.days)}
                  disabled={detailLoading}
                >
                  {w.label}
                </button>
              ))}
            </div>

            {detailLoading ? (
              <p>Loading history...</p>
            ) : (
              <>
                <div className="cpanel-detail-stats">
                  <div className="cpanel-stat">
                    <span className="cpanel-stat-value">{detail.average ?? '—'}</span>
                    <span className="cpanel-stat-label">Average NPROC</span>
                  </div>
                  <div className="cpanel-stat">
                    <span className="cpanel-stat-value">{detail.max ?? '—'}</span>
                    <span className="cpanel-stat-label">Peak NPROC</span>
                  </div>
                  <div className="cpanel-stat">
                    <span className="cpanel-stat-value">{detail.min ?? '—'}</span>
                    <span className="cpanel-stat-label">Lowest NPROC</span>
                  </div>
                  <div className="cpanel-stat">
                    <span className="cpanel-stat-value">{detail.limit ?? '—'}</span>
                    <span className="cpanel-stat-label">Limit</span>
                  </div>
                </div>

                {detail.checks.length === 0 ? (
                  <p className="cpanel-none">No checks recorded in this window yet.</p>
                ) : (
                  <div className="cpanel-bars">
                    {bucketForChart(detail.checks).map((bucket, index) => {
                      const limit = detail.limit || 1;
                      const ratio = bucket.value === null ? 0 : Math.min(1, bucket.value / limit);
                      const level = bucket.value === null ? 'unknown' : ratio >= 1 ? 'maxed' : ratio >= 0.8 ? 'warn' : 'ok';
                      return (
                        <span
                          key={index}
                          className={`cpanel-bar cpanel-bar-${level}`}
                          style={{ height: `${Math.max(4, Math.round(ratio * 100))}%` }}
                          title={`${new Date(bucket.at).toLocaleString()} — ${bucket.value === null ? 'no data' : `${bucket.value}/${detail.limit ?? '?'} NPROC`}`}
                        ></span>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </PageShell>
  );
}

export default CPanel;
